import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, Inject, Optional, HostBinding } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/core/services/api.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { DataService } from 'src/app/core/services/data.service';
import { PrimeNGConfig } from 'primeng/api';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'app-user-data-request',
  templateUrl: './user-data-request.component.html',
  styleUrls: ['./user-data-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class UserDataRequestComponent implements OnInit, OnDestroy {
  @Input() showModal: boolean = false;
  @Input() logoPath: string = 'assets/images/LogoNuovo_login.png';
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() modalClosed = new EventEmitter<void>();

  userDataForm: FormGroup;
  selectedFileName: string = '';
  selectedFile: File | null = null;
  privacyDocumentOpened: boolean = false;
  privacyPolicyOpened: boolean = false;
  dataActInfoExpanded: boolean = false; // Toggle per info Data Act in page mode

  // Variabili per modale di messaggio interna
  showResponseModal: boolean = false;
  responseModalTitle: string = '';
  responseModalMessage: string = '';
  responseModalType: 'success' | 'error' = 'success';

  // Variabile per modale "invio in corso"
  showSendingModal: boolean = false;

  // Configurazione locale per p-calendar
  calendarLocale: any = {};
  tenantColorClass: string;

  // Colore dinamico del tenant per la modale "invio in corso" e il dialog di risposta
  // (elementi fuori dal container tematizzato, esposti tramite CSS custom property)
  @HostBinding('style.--modal-color-info') mainColor: string;

  private destroy$ = new Subject<void>();
  private customerUid: string = '';
  private ragioneSocialeCustomer: string = '';

  titleOptions = [
    { value: 'sig', labelKey: 'userDataRequest.titleSig' },
    { value: 'sig.ra', labelKey: 'userDataRequest.titleSigRa' },
    { value: 'dott', labelKey: 'userDataRequest.titleDott' },
    { value: 'dott.ssa', labelKey: 'userDataRequest.titleDottSsa' },
    { value: 'ing', labelKey: 'userDataRequest.titleIng' },
    { value: 'prof', labelKey: 'userDataRequest.titleProf' }
  ];

  countryOptions = [
    { value: 'IT', labelKey: 'userDataRequest.countryItaly' },
    { value: 'FR', labelKey: 'userDataRequest.countryFrance' },
    { value: 'DE', labelKey: 'userDataRequest.countryGermany' },
    { value: 'ES', labelKey: 'userDataRequest.countrySpain' },
    { value: 'altro', labelKey: 'userDataRequest.countryOthers' }
  ];

  userTypeOptions = [
    { value: 'proprietario', labelKey: 'userDataRequest.userTypeOwner' },
    { value: 'noleggiatore', labelKey: 'userDataRequest.userTypeRenter' },
    { value: 'dipendente', labelKey: 'userDataRequest.userTypeEmployee' },
    { value: 'cliente', labelKey: 'userDataRequest.userTypeCustomer' }
  ];

  informationOptions = [
    {
      value: 'accesso',
      labelKey: 'userDataRequest.infoAccessLabel',
      descriptionKey: 'userDataRequest.infoAccessDescription'
    },
    {
      value: 'cancellazione',
      labelKey: 'userDataRequest.infoDeletionLabel',
      descriptionKey: 'userDataRequest.infoDeletionDescription'
    },
    {
      value: 'portabilita',
      labelKey: 'userDataRequest.infoPortabilityLabel',
      descriptionKey: 'userDataRequest.infoPortabilityDescription'
    }
  ];

  documentTypeOptions = [
    { value: 'carta_identita', labelKey: 'userDataRequest.documentTypeId' },
    { value: 'passaporto', labelKey: 'userDataRequest.documentTypePassport' }
  ];

  dataFormatOptions = [
    { value: 'json', labelKey: 'userDataRequest.formatJson' },
    { value: 'csv', labelKey: 'userDataRequest.formatCsv' },
    { value: 'xml', labelKey: 'userDataRequest.formatXml' },
    { value: 'pdf', labelKey: 'userDataRequest.formatPdf' }
  ];

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private utilsService: UtilsService,
    private dataService: DataService,
    private primengConfig: PrimeNGConfig,
    @Optional() public dialogRef: MatDialogRef<UserDataRequestComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.initializeForm();

    // Se aperto come dialog, imposta showModal a true e il logoPath dai data
    if (this.dialogRef) {
      this.showModal = true;
      if (this.data?.logoPath) {
        this.logoPath = this.data.logoPath;
      }
    } else {
      // Se usato come rotta standalone, mostra comunque il componente
      this.showModal = true;
    }
  }

  ngOnInit(): void {
    this.setupDateValidation();
    this.setupCalendarLocale();

    // Imposta il colore dinamico basato su tenant
    this.tenantColorClass = this.utilsService.getStylesheetString() + '-theme';
    this.mainColor = this.utilsService.getMainColor();

    const userCustomer = this.dataService.getCurrentUserInfo();
    this.customerUid = userCustomer.customer.uid;
    this.ragioneSocialeCustomer = userCustomer.customer.name;
 this.translateService.setDefaultLang(this.dataService.getLanguage());
    // Assicurati che la lingua sia impostata
    // const currentLang = this.translateService.currentLang || this.translateService.defaultLang || 'it';
    // console.log('Current Language:', currentLang);
    // if (!this.translateService.currentLang) {
    //   this.translateService.use(currentLang);
    // }

    // Forza il change detection quando la lingua cambia
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setupCalendarLocale();
        this.cdr.markForCheck();
      });

    // Forza il change detection iniziale per assicurarsi che le traduzioni siano caricate
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.userDataForm = this.fb.group({
      titolo: ['', [Validators.required]],
      nome: ['', [Validators.required]],
      cognome: ['', [Validators.required]],
      partita_iva: ['', [Validators.required]],
      paese: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      cellulare: ['', [Validators.required]],
      sono_un: ['', [Validators.required]],
      targa_vin: [''],
      informazioni: ['', [Validators.required]],
      periodo_da: [''],
      periodo_a: [''],
      richiesta_specifica: ['', [Validators.required]],
      tipo_documento: ['', [Validators.required]],
      documento: ['', [Validators.required]],
      informativa_data_act: [{ value: false, disabled: true }],
      informativa_privacy: [{ value: false, disabled: true }]
    });
  }

  private setupDateValidation(): void {
    this.userDataForm.get('periodo_da')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(startDate => {
        if (startDate) {
          const endDateControl = this.userDataForm.get('periodo_a');
          if (endDateControl?.value && new Date(endDateControl.value) < new Date(startDate)) {
            endDateControl.setValue('');
          }
        }
      });
  }

  private setupCalendarLocale(): void {
    const lang = this.dataService.getLanguage();

    const locales: any = {
      it: {
        firstDayOfWeek: 1,
        dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
        dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
        monthNames: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
        monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
        today: 'Oggi',
        clear: 'Cancella'
      },
      en: {
        firstDayOfWeek: 0,
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        today: 'Today',
        clear: 'Clear'
      },
      fr: {
        firstDayOfWeek: 1,
        dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
        monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
        today: 'Aujourd\'hui',
        clear: 'Effacer'
      }
    };

    this.calendarLocale = locales[lang] || locales['it'];
    this.primengConfig.setTranslation(this.calendarLocale);
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.translateService.get('userDataRequest.errorFileSize')
          .subscribe(message => alert(message || 'Il file non può essere più grande di 10MB'));
        return;
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.translateService.get('userDataRequest.errorFileType')
          .subscribe(message => alert(message || 'Formati accettati: PDF, JPG, PNG'));
        return;
      }

      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.userDataForm.get('documento')?.setValue(file.name);
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('documento-input') as HTMLInputElement;
    fileInput?.click();
  }

  getFileDisplayName(): string {
    if (this.selectedFileName) {
      return this.selectedFileName;
    }
    return this.translateService.instant('userDataRequest.noFileSelected') || 'Nessun file selezionato';
  }

  hasFileSelected(): boolean {
    return !!this.selectedFileName;
  }

  openPrivacyDocument(): void {
    const stylesheet = this.utilsService.getStylesheetString();
    let language = this.dataService.getLanguage();

    // Solo italiano e inglese hanno il documento, altre lingue usano inglese di default
    // if (language !== 'it' && language !== 'en') {
    //   language = 'en';
    // }

    const privacyFile = `assets/documents/${stylesheet}_policy_${language}.pdf`;

    window.open(privacyFile, '_blank');
    this.privacyDocumentOpened = true;
    this.userDataForm.get('informativa_data_act')?.enable();
  }

  openPrivacyPolicy(): void {
    const stylesheet = this.utilsService.getStylesheetString();
    let language = this.dataService.getLanguage();

    // Solo italiano e inglese hanno il documento, altre lingue usano inglese di default
    if (language !== 'it' && language !== 'en') {
      language = 'en';
    }

    const privacyFile = `assets/documents/${stylesheet}_privacy_${language}.pdf`;

    window.open(privacyFile, '_blank');
    this.privacyPolicyOpened = true;
    this.userDataForm.get('informativa_privacy')?.enable();
  }

  onSubmit(): void {
    if (this.userDataForm.valid) {
      const formValue = this.userDataForm.value;

      // Converti il file in base64 se presente
      if (this.selectedFile) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64File = (reader.result as string).split(',')[1]; // Rimuove il prefisso "data:...;base64,"
          this.sendRequest(formValue, base64File);
        };
        reader.readAsDataURL(this.selectedFile);
      } else {
        this.sendRequest(formValue, '');
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private sendRequest(formValue: any, base64File: string): void {
    // Mostra modale "Invio in corso"
    this.showSendingModal = true;
    this.cdr.detectChanges();

    // Formatta le date in formato localizzato (solo data, senza ora)
    let dataDa = ' - ';
    let dataA = ' - ';

    const lang = this.dataService.getLanguage();
    const locale = lang === 'en' ? 'en-US' : lang === 'fr' ? 'fr-FR' : lang === 'de' ? 'de-DE' : lang === 'es' ? 'es-ES' : 'it-IT';

    if (formValue.periodo_da) {
      const startDate = new Date(formValue.periodo_da);
      dataDa = startDate.toLocaleDateString(locale);
    }

    if (formValue.periodo_a) {
      const endDate = new Date(formValue.periodo_a);
      dataA = endDate.toLocaleDateString(locale);
    }

    // Ottieni i testi tradotti delle select invece dei valori
    const titoloText = this.getTranslatedText('titolo', formValue.titolo);
    const paeseText = this.getCountryText(formValue.paese);
    const sonoUnText = this.getTranslatedText('sono_un', formValue.sono_un);
    const informazioniText = this.getTranslatedText('informazioni', formValue.informazioni);
    const tipoDocumentoText = this.getTranslatedText('tipo_documento', formValue.tipo_documento);

    const requestData = {
      FileName: this.selectedFileName,
      Email: formValue.email,
      Titolo: titoloText,
      Nome: formValue.nome,
      Cognome: formValue.cognome,
      Piva: formValue.partita_iva,
      PaeseResidenza: paeseText,
      Telefono: formValue.cellulare,
      Professione: sonoUnText,
      Vin: formValue.targa_vin,
      TipoInfo: informazioniText,
      DataDa: dataDa,
      DataA: dataA,
      Richiesta: formValue.richiesta_specifica,
      TipoDoc: tipoDocumentoText,
      RagioneSociale: this.ragioneSocialeCustomer
    };

    this.apiService.sendUserDataRequest(this.customerUid, requestData, base64File)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('Richiesta inviata con successo:', response);

          this.formSubmitted.emit(requestData);

          // Nascondi modale "invio in corso"
          this.showSendingModal = false;

          // Invalida il form solo in caso di successo
          this.userDataForm.reset();
          this.selectedFile = null;
          this.selectedFileName = '';
          this.privacyDocumentOpened = false;
          this.privacyPolicyOpened = false;

          // Mostra messaggio di successo nella modale interna
          this.responseModalType = 'success';
          this.responseModalTitle = this.translateService.instant('userDataRequest.successTitle');
          this.responseModalMessage = this.translateService.instant('userDataRequest.successMessage')

          this.showResponseModal = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Errore invio richiesta:', error);

          // Nascondi modale "invio in corso"
          this.showSendingModal = false;

          // Mostra messaggio di errore nella modale interna (form NON viene invalidato)
          this.responseModalType = 'error';
          this.responseModalTitle = this.translateService.instant('userDataRequest.errorTitle');
          this.responseModalMessage = this.translateService.instant('userDataRequest.errorMessage');
          this.showResponseModal = true;
          this.cdr.detectChanges();
        }
      });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.userDataForm.controls).forEach(key => {
      const control = this.userDataForm.get(key);
      control?.markAsTouched();
    });
  }

  closeModal(): void {
    // Non resettiamo più il form qui, viene fatto solo dopo l'invio con successo
    this.showModal = false;

    // Se è un dialog Material, chiudi il dialog
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      // Altrimenti emetti l'evento per il modal tradizionale
      this.modalClosed.emit();
    }
  }

  onCancel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.closeModal();
  }

  onModalOkClick(): void {
    // // Chiudi la modale di risposta e poi chiudi tutto
     this.showResponseModal = false;
    // this.closeModal();
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.userDataForm.get(fieldName);
    return !!(field?.invalid && (field?.dirty || field?.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.userDataForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return this.translateService.instant('userDataRequest.fieldRequired') || 'Campo obbligatorio';
      }
      if (field.errors['email']) {
        return this.translateService.instant('userDataRequest.invalidEmail') || 'Email non valida';
      }
    }
    return '';
  }

  isFormValid(): boolean {
    // Debug per capire cosa blocca il form
    console.log('Form valid:', this.userDataForm.valid);
    console.log('Form invalid:', this.userDataForm.invalid);
    console.log('Form errors:', this.userDataForm.errors);

    // Controlla ogni campo
    Object.keys(this.userDataForm.controls).forEach(key => {
      const control = this.userDataForm.get(key);
      if (control?.invalid) {
        console.log(`Campo invalido: ${key}`, control.errors);
      }
    });

    // Controlla la validità del form
    if (this.userDataForm.invalid) {
      return false;
    }

    // Controlla che entrambi i checkbox siano stati abilitati e spuntati
    const dataActControl = this.userDataForm.get('informativa_data_act');
    const privacyControl = this.userDataForm.get('informativa_privacy');

    console.log('Data Act enabled:', dataActControl?.enabled, 'value:', dataActControl?.value);
    console.log('Privacy enabled:', privacyControl?.enabled, 'value:', privacyControl?.value);

    const dataActValid = !!(dataActControl?.enabled && dataActControl?.value === true);
    const privacyValid = !!(privacyControl?.enabled && privacyControl?.value === true);

    console.log('Data Act valid:', dataActValid);
    console.log('Privacy valid:', privacyValid);
    console.log('Final result:', dataActValid && privacyValid);

    return dataActValid && privacyValid;
  }

  getInfoDescription(value: string): string {
    if (!value) return '';
    const option = this.informationOptions.find(opt => opt.value === value);
    return option ? this.translateService.instant(option.descriptionKey) : '';
  }

  private getTranslatedText(fieldName: string, value: string): string {
    if (!value) return '';

    let options: any[] = [];

    switch(fieldName) {
      case 'titolo':
        options = this.titleOptions;
        break;
      case 'sono_un':
        options = this.userTypeOptions;
        break;
      case 'informazioni':
        options = this.informationOptions;
        break;
      case 'tipo_documento':
        options = this.documentTypeOptions;
        break;
    }

    const option = options.find(opt => opt.value === value);
    if (option?.labelKey) {
      return this.translateService.instant(option.labelKey);
    } else if (option?.descriptionKey) {
      return this.translateService.instant(option.descriptionKey);
    }

    return value;
  }

  private getCountryText(value: string): string {
    if (!value) return '';

    const country = this.countryOptions.find(opt => opt.value === value);
    if (country?.labelKey) {
      return this.translateService.instant(country.labelKey);
    }

    return value;
  }
}
